// Identifier section for System Architecture - 135 lines ✅
import { Key } from 'lucide-react'
import { Badge } from '@/components/ui/badge'

interface IdentifierType {
  name: string
  type: string
  description: string
  example: string
  usage: string
}

const identifiers: IdentifierType[] = [
  {
    name: 'UUID v4',
    type: 'Primary',
    description: 'All entity primary keys',
    example: '550e8400-e29b-41d4-a716-446655440000',
    usage: 'gen_random_uuid() for all table IDs'
  },
  {
    name: 'Email',
    type: 'Unique',
    description: 'User unique identifier',
    example: 'nelli.tarkka@kaycoo.ai',
    usage: 'User authentication and lookup'
  },
  {
    name: 'organization_id',
    type: 'Foreign Key',
    description: 'Multi-tenant isolation key',
    example: '123e4567-e89b-12d3-a456-426614174000',
    usage: 'Present in all tenant-scoped tables'
  },
  {
    name: 'D365 Version',
    type: 'External',
    description: 'Microsoft D365 F&O version string',
    example: '10.0.42',
    usage: 'Version matching in KB search'
  },
  {
    name: 'D365 Module',
    type: 'External',
    description: 'D365 functional module code',
    example: 'Finance, SCM, Commerce',
    usage: 'Module filtering and expert routing'
  },
  {
    name: 'Storage Path',
    type: 'External',
    description: 'Supabase Storage location',
    example: 'customer-documents/org-id/file.pdf',
    usage: 'Document retrieval from storage'
  },
  {
    name: 'Agent ID',
    type: 'External',
    description: 'Kaycoo platform agent UUID',
    example: 'd365-issue-recorder',
    usage: 'AI agent execution requests'
  },
  {
    name: 'Application ID',
    type: 'External',
    description: 'Platform application identifier',
    example: 'd365-ai-assistant',
    usage: 'API authentication header'
  }
]

const compositeKeys = [
  {
    name: '(organization_id, entity_type, entity_id)',
    table: 'kg_node',
    description: 'Unique constraint ensuring one KG node per entity per org'
  },
  {
    name: '(document_id, image_index)',
    table: 'document_images',
    description: 'Unique constraint for extracted images from documents'
  },
  {
    name: '(organization_id, email)',
    table: 'users',
    description: 'User lookup by email within organization'
  }
]

export function IdentifierSection() {
  return (
    <div className="space-y-6">
      <div className="rounded-lg border border-kaycoo-teal-dark bg-white p-6">
        <div className="mb-4 flex items-center gap-2">
          <Key className="h-6 w-6 text-kaycoo-teal-dark" />
          <h2 className="text-2xl font-bold text-kaycoo-teal-dark">Identifiers & Keys</h2>
        </div>
        <p className="text-kaycoo-charcoal-2">
          Unique identifiers, keys, and external references used throughout the system.
        </p>
      </div>

      {/* Primary Identifiers */}
      <div className="rounded-lg border border-kaycoo-teal-dark bg-white p-6">
        <h3 className="mb-4 text-lg font-semibold text-kaycoo-teal-dark">Identifier Types</h3>
        <div className="space-y-3">
          {identifiers.map((id) => (
            <div key={id.name} className="rounded border border-kaycoo-teal bg-kaycoo-teal-pale p-4">
              <div className="mb-2 flex items-start justify-between">
                <div>
                  <div className="font-mono font-semibold text-kaycoo-teal-dark">{id.name}</div>
                  <div className="text-sm text-kaycoo-charcoal-2">{id.description}</div>
                </div>
                <Badge variant="outline">{id.type}</Badge>
              </div>
              <div className="mt-2 rounded bg-gray-50 p-2">
                <div className="text-xs font-medium text-kaycoo-charcoal-2">Example:</div>
                <code className="text-xs text-kaycoo-teal-dark">{id.example}</code>
              </div>
              <div className="mt-2 text-xs text-kaycoo-charcoal-2">
                <span className="font-medium">Usage:</span> {id.usage}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Composite Keys */}
      <div className="rounded-lg border border-kaycoo-teal-dark bg-white p-6">
        <h3 className="mb-4 text-lg font-semibold text-kaycoo-teal-dark">Composite Keys</h3>
        <div className="space-y-3">
          {compositeKeys.map((key, i) => (
            <div key={i} className="rounded border border-kaycoo-teal bg-white p-3">
              <div className="mb-1 font-mono text-sm font-semibold text-kaycoo-teal-dark">
                {key.name}
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Badge className="bg-kaycoo-teal text-white">{key.table}</Badge>
                <span className="text-kaycoo-charcoal-2">{key.description}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ID Generation Info */}
      <div className="rounded-lg border border-kaycoo-teal-dark bg-kaycoo-teal-pale p-6">
        <h3 className="mb-3 text-lg font-semibold text-kaycoo-teal-dark">ID Generation Strategy</h3>
        <div className="space-y-2 text-sm text-kaycoo-charcoal-2">
          <div>
            <span className="font-medium">Database:</span> PostgreSQL <code>gen_random_uuid()</code> function generates UUID v4
          </div>
          <div>
            <span className="font-medium">Benefits:</span> Globally unique, no collision risk, distributed-friendly
          </div>
          <div>
            <span className="font-medium">Format:</span> 8-4-4-4-12 hexadecimal format (36 characters with hyphens)
          </div>
        </div>
      </div>
    </div>
  )
}
